import { useCallback, useEffect, useRef, useState } from "react";
import {
  applyFreeDelivery as applyFreeDeliveryCore,
  computeCardSurcharge as computeCardSurchargeCore,
  type CardSurchargeConfig,
} from "@workspace/pricing";

export interface DeliveryAddress {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  reference?: string;
  lat: number | null;
  lng: number | null;
  formattedAddress?: string;
}

export interface DeliveryFeeResult {
  fee: number | null;
  distanceKm: number | null;
  noDelivery: boolean;
  neighborhood?: string | null;
  neighborhoods?: Array<{ name: string; fee: number }>;
  acceptUnlisted?: boolean;
  unlistedFee?: number | null;
  /** Frete a combinar (distância acima da última faixa, ou bairro fora da lista com «Calcular após o pedido»):
   *  `fee` null, não grátis. */
  feePending?: boolean;
}

/** Regra do bairro fora da lista: «Definir um valor» (`fixed`) ou «Calcular após o pedido» (`on_request`). */
export interface NeighborhoodMeta {
  acceptUnlisted: boolean;
  unlistedFee: number | null;
  unlistedMode: "fixed" | "on_request";
  /** A lista já chegou da API (lista vazia de verdade ≠ ainda carregando). */
  loaded: boolean;
}

export type DeliveryFeeType = "fixed" | "distance" | "neighborhood" | "on_request";

interface UseDeliveryFeeArgs {
  slug: string;
  unitId?: string | null;
  store: any;
  address: DeliveryAddress;
  setAddress: (updater: (prev: DeliveryAddress) => DeliveryAddress) => void;
  deliveryMode: "retirada" | "entrega";
  enabled?: boolean;
}

export interface UseDeliveryFeeReturn {
  feeType: DeliveryFeeType;
  deliveryFee: number;
  feeLoading: boolean;
  noDeliveryBlocked: boolean;
  feeNotResolved: boolean;
  /** Frete a combinar (distância acima da última faixa, ou bairro fora da lista com «Calcular após o pedido»):
   *  taxa 0 agora, o pedido sai com o frete pendente. */
  feePending: boolean;
  freeDeliveryApplied: boolean;
  dynamicFee: DeliveryFeeResult | null;
  selectedNeighborhood: string;
  setSelectedNeighborhood: (n: string) => void;
  neighborhoodList: Array<{ name: string; fee: number }>;
  neighborhoodMeta: NeighborhoodMeta;
  googleMapsKey: string;
  lookupCep: (cep: string) => Promise<void>;
  /** Endereço que veio pronto (o do pedido aberto para edição, um salvo do cliente): o CEP dele não passa pela busca,
   *  que troca rua e bairro pelos do CEP sem ninguém mexer. */
  marcarCepComoBuscado: (cep: string) => void;
}

function getApiBase(): string {
  // Em prod com subdomínio split (api.vendanozap.app), VITE_API_URL é injetado
  // no build. Em dev (proxy Vite -> localhost:8080), chamadas relativas batem
  // no proxy via BASE_URL.
  const fromEnv = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, "");
  return (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
}

export function useDeliveryFee({
  slug,
  unitId,
  store,
  address,
  setAddress,
  deliveryMode,
  enabled = true,
}: UseDeliveryFeeArgs): UseDeliveryFeeReturn {
  const feeType: DeliveryFeeType = ((store as any)?.deliveryFeeType ?? "fixed") as DeliveryFeeType;
  const freeDeliveryAbove = parseFloat((store as any)?.freeDeliveryAbove ?? "0") || 0;

  const [dynamicFee, setDynamicFee] = useState<DeliveryFeeResult | null>(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [neighborhoodList, setNeighborhoodList] = useState<Array<{ name: string; fee: number }>>([]);
  const [neighborhoodMeta, setNeighborhoodMeta] = useState<NeighborhoodMeta>({ acceptUnlisted: false, unlistedFee: null, unlistedMode: "fixed", loaded: false });
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");
  const [googleMapsKey, setGoogleMapsKey] = useState("");

  const distanceFetchRef = useRef<string | null>(null);
  const cepLookupRef = useRef<string | null>(null);
  // Ordena as respostas da cotação: só a MAIS RECENTE pode aplicar (resposta
  // velha chegando por último sobrescrevia a nova — taxa exibida stale).
  const distanceFetchSeqRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    fetch(`${getApiBase()}/api/catalog/config/maps-key`)
      .then((r) => r.json())
      .then((d) => { if (d.key) setGoogleMapsKey(d.key); })
      .catch(() => {});
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (feeType !== "neighborhood" || !store || deliveryMode !== "entrega") return;
    const qs = unitId ? `&unitId=${unitId}` : "";
    fetch(`${getApiBase()}/api/catalog/${slug}/delivery-fee?${qs}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.neighborhoods) {
          // Atualizar só se mudou de fato — evita disparar useEffects dependentes
          // (em particular o de seleção de bairro) por igualdade de valor.
          setNeighborhoodList((prev) => {
            if (prev.length === data.neighborhoods.length &&
                prev.every((p: { name: string; fee: number }, i: number) =>
                  p.name === data.neighborhoods[i].name && p.fee === data.neighborhoods[i].fee)) {
              return prev;
            }
            return data.neighborhoods;
          });
          const nextMeta: NeighborhoodMeta = {
            acceptUnlisted: !!data.acceptUnlisted,
            unlistedFee: data.unlistedFee ?? null,
            unlistedMode: data.unlistedMode === "on_request" ? "on_request" : "fixed",
            loaded: true,
          };
          setNeighborhoodMeta((prev) =>
            prev.acceptUnlisted === nextMeta.acceptUnlisted && prev.unlistedFee === nextMeta.unlistedFee &&
            prev.unlistedMode === nextMeta.unlistedMode && prev.loaded
              ? prev
              : nextMeta,
          );
        }
      })
      .catch(() => {});
  }, [enabled, feeType, store, deliveryMode, slug, unitId]);

  const lookupCep = useCallback(async (cep: string) => {
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    if (cepLookupRef.current === clean) return;
    cepLookupRef.current = clean;
    try {
      const qs = unitId ? `&unitId=${unitId}` : "";
      const resp = await fetch(`${getApiBase()}/api/catalog/${slug}/viacep?zipCode=${clean}${qs}`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        setAddress((prev) => ({
          ...prev,
          street: data.street || prev.street,
          neighborhood: data.neighborhood || prev.neighborhood,
          city: data.city || prev.city,
          state: data.state || prev.state,
        }));
        if (feeType === "neighborhood" && data.neighborhood) {
          setSelectedNeighborhood(data.neighborhood);
        }
      }
    } catch {}
  }, [slug, unitId, feeType, setAddress]);

  const marcarCepComoBuscado = useCallback((cep: string) => {
    cepLookupRef.current = cep.replace(/\D/g, "");
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const clean = address.zipCode.replace(/\D/g, "");
    if (clean.length === 8) {
      lookupCep(clean);
    }
  }, [enabled, address.zipCode, lookupCep]);

  useEffect(() => {
    if (!enabled) return;
    if (feeType !== "distance" || deliveryMode !== "entrega") return;
    const hasCoords = address.lat != null && address.lng != null;
    const clean = address.zipCode.replace(/\D/g, "");
    if (!hasCoords && clean.length !== 8) return;
    const key = hasCoords ? `${address.lat}|${address.lng}` : `${clean}|${address.street}|${address.number}|${address.city}|${address.neighborhood}`;
    if (distanceFetchRef.current === key) return;
    distanceFetchRef.current = key;
    const seq = ++distanceFetchSeqRef.current;
    setFeeLoading(true);
    const params = new URLSearchParams();
    if (hasCoords) {
      params.set("lat", String(address.lat));
      params.set("lng", String(address.lng));
    }
    if (clean) params.set("zipCode", clean);
    if (address.street) params.set("street", address.street);
    if (address.number) params.set("number", address.number);
    if (address.city) params.set("city", address.city);
    if (address.neighborhood) params.set("neighborhood", address.neighborhood);
    if (unitId) params.set("unitId", unitId);
    fetch(`${getApiBase()}/api/catalog/${slug}/delivery-fee?${params.toString()}`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) { if (seq === distanceFetchSeqRef.current) setDynamicFee(null); return null; }
        return r.json();
      })
      .then((data) => { if (data && seq === distanceFetchSeqRef.current) setDynamicFee(data); })
      .catch(() => { if (seq === distanceFetchSeqRef.current) setDynamicFee(null); })
      .finally(() => { if (seq === distanceFetchSeqRef.current) setFeeLoading(false); });
  }, [enabled, feeType, deliveryMode, address.zipCode, address.street, address.number, address.city, address.neighborhood, address.lat, address.lng, slug, unitId]);

  useEffect(() => {
    if (!enabled) return;
    if (feeType !== "neighborhood") return;

    // Helper: só re-set se mudou de fato. Evita loop infinito quando setAddress
    // do componente pai não está estável entre renders (React error #185).
    const setFeeIfChanged = (next: DeliveryFeeResult) => {
      setDynamicFee((prev) => {
        if (prev && prev.fee === next.fee && prev.noDelivery === next.noDelivery && prev.neighborhood === next.neighborhood && !!prev.feePending === !!next.feePending) {
          return prev;
        }
        return next;
      });
    };
    const setNeighborhoodIfChanged = (value: string) => {
      setAddress((prev) => (prev.neighborhood === value ? prev : { ...prev, neighborhood: value }));
    };
    // Bairro fora da lista (Pedro, 15/10/2026): com «Calcular após o pedido» o frete fica pendente, como no pedido do
    // link — antes o PDV punha a taxa do «Definir um valor» (ou R$ 0) e gravava sem pendência, e o «Enviar frete» não
    // aparecia depois.
    const foraDaLista = (bairro: string | null): DeliveryFeeResult =>
      neighborhoodMeta.unlistedMode === "on_request"
        ? { fee: null, feePending: true, distanceKm: null, noDelivery: false, neighborhood: bairro }
        : { fee: neighborhoodMeta.unlistedFee ?? 0, distanceKm: null, noDelivery: false, neighborhood: bairro };

    // Lista vazia: todo bairro está fora da lista, então a taxa sai da regra do não cadastrado sem esperar o bairro — o
    // valor definido ou, com «Calcular após o pedido», o frete pendente. Antes a taxa nunca resolvia e o «Finalizar»
    // ficava travado (78 lojas com «Definir um valor», Pedro 15/10/2026: cobrar o valor, como o link).
    if (neighborhoodMeta.loaded && neighborhoodList.length === 0 && neighborhoodMeta.acceptUnlisted) {
      setFeeIfChanged(foraDaLista(null));
      if (selectedNeighborhood && selectedNeighborhood !== "__other__") setNeighborhoodIfChanged(selectedNeighborhood);
      return;
    }
    if (!selectedNeighborhood) return;

    if (selectedNeighborhood === "__other__") {
      setFeeIfChanged(foraDaLista(""));
      setNeighborhoodIfChanged("");
      return;
    }
    if (neighborhoodList.length === 0) {
      setNeighborhoodIfChanged(selectedNeighborhood);
      return;
    }
    const normalize = (s: string) =>
      (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
    const target = normalize(selectedNeighborhood);
    const match = neighborhoodList.find((n) => normalize(n.name) === target);
    if (match) {
      setFeeIfChanged({ fee: match.fee, distanceKm: null, noDelivery: false, neighborhood: match.name });
    } else if (neighborhoodMeta.acceptUnlisted) {
      setFeeIfChanged(foraDaLista(selectedNeighborhood));
    } else {
      setFeeIfChanged({ fee: null, distanceKm: null, noDelivery: true, neighborhood: selectedNeighborhood });
    }
    setNeighborhoodIfChanged(selectedNeighborhood);
  }, [enabled, selectedNeighborhood, neighborhoodList, neighborhoodMeta, feeType, setAddress]);

  let deliveryFee = 0;
  let freeDeliveryApplied = false;

  if (deliveryMode === "entrega") {
    if (feeType === "fixed") {
      deliveryFee = parseFloat((store as any)?.deliveryFee ?? "0") || 0;
    } else if (feeType === "on_request") {
      deliveryFee = 0;
    } else if (dynamicFee && !dynamicFee.noDelivery && dynamicFee.fee !== null) {
      deliveryFee = dynamicFee.fee;
    }
  }

  const noDeliveryBlocked = deliveryMode === "entrega" && feeType !== "fixed" && feeType !== "on_request" && dynamicFee?.noDelivery === true;
  const feeNotResolved = deliveryMode === "entrega" && feeType !== "fixed" && feeType !== "on_request" && (dynamicFee === null || feeLoading);
  const feePending = deliveryMode === "entrega" && (feeType === "distance" || feeType === "neighborhood") && !feeLoading && dynamicFee?.feePending === true;

  return {
    feeType,
    deliveryFee,
    feeLoading,
    noDeliveryBlocked,
    feeNotResolved,
    feePending,
    freeDeliveryApplied,
    dynamicFee,
    selectedNeighborhood,
    setSelectedNeighborhood,
    neighborhoodList,
    neighborhoodMeta,
    googleMapsKey,
    lookupCep,
    marcarCepComoBuscado,
  };
}

export function applyFreeDelivery(deliveryFee: number, subtotal: number, store: any): { fee: number; freeApplied: boolean } {
  return applyFreeDeliveryCore({
    deliveryFee,
    subtotalAfterDiscount: subtotal,
    freeDeliveryAbove: (store as any)?.freeDeliveryAbove,
  });
}

export function computeCardSurcharge(store: any, paymentMethod: string, baseTotal: number, deliveryFee: number): number {
  const cfgMap = ((store as any)?.cardSurchargeConfig ?? null) as Record<string, CardSurchargeConfig | undefined> | null;
  return computeCardSurchargeCore(cfgMap, paymentMethod, baseTotal, deliveryFee);
}
